import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MessageSquare, MessageSquarePlus } from "lucide-react";
import { InstallAppButton } from "@/components/InstallGuide";
import { AskDispensaryDialog } from "@/components/AskDispensaryDialog";
import { EmailSubscribe } from "@/components/EmailSubscribe";
import { useBrandLogo } from "@/hooks/useBrandLogo";

const CONTACT_EMAIL = "BudLink@LinkLitePro.com";
const CONTACT_PHONE_RAW = "4845773677"; // for tel:/sms: links
const CONTACT_PHONE_DISPLAY = "(484) 577-3677";

export const SiteFooter = () => {
  const [askOpen, setAskOpen] = useState(false);
  const brand = useBrandLogo();
  return (
  <footer className="border-t border-border mt-24">
    <div className="container-narrow py-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-8 text-sm">
      <div className="lg:col-span-2">
        <img
          src={brand.src}
          alt={brand.label}
          className="w-20 h-20 rounded-full object-cover ring-2 ring-primary/30 mb-3"
        />
        <p className="text-foreground/60 text-xs leading-relaxed max-w-xs mb-2">
          Find legal strains near you. Now live in Pennsylvania, Colorado &amp;
          Oregon, with 36 more states unlocking as partner APIs come online.
          Never scraped — always via owner-provided menus or partner APIs.
          21+ only.
        </p>
        <p className="text-foreground/50 text-[11px] leading-relaxed max-w-xs mb-4" data-testid="footer-account-tagline">
          Free to browse — sign in to save favorites across devices.
        </p>
        <InstallAppButton
          testId="footer-install-btn"
          label="Install on your phone"
          variant="ghost"
          className="text-xs h-9"
        />
        <div className="mt-6 max-w-xs">
          <div className="text-[11px] uppercase tracking-[0.2em] text-foreground/60 mb-2">
            Sales alerts
          </div>
          <EmailSubscribe
            variant="inline"
            source="footer"
            testId="footer-email-subscribe"
          />
          <p className="text-[10px] text-muted-foreground mt-1.5 leading-relaxed">
            Email only. No name, no phone. Unsubscribe in one tap.
          </p>
        </div>
      </div>
      <div>
        <div className="eyebrow mb-3">Consumer</div>
        <ul className="space-y-2 text-foreground/70 text-sm">
          <li><Link to="/" className="hover:text-foreground">Strain search</Link></li>
          <li><Link to="/dispensaries" className="hover:text-foreground">Dispensary directory</Link></li>
          <li><Link to="/account" className="hover:text-foreground">My saved</Link></li>
          <li>
            <button
              type="button"
              onClick={() => setAskOpen(true)}
              data-testid="footer-ask-dispensary"
              className="hover:text-foreground inline-flex items-center gap-1.5 text-left"
            >
              <MessageSquarePlus className="w-3.5 h-3.5" />
              Don&apos;t see your shop?
            </button>
          </li>
        </ul>
      </div>
      <div>
        <div className="eyebrow mb-3">Dispensary</div>
        <ul className="space-y-2 text-foreground/70 text-sm">
          <li><Link to="/onboarding" className="hover:text-foreground" data-testid="footer-onboarding-link">Onboarding guide</Link></li>
          <li><Link to="/dispensary/signup" className="hover:text-foreground">30-day free trial</Link></li>
          <li><Link to="/pricing" className="hover:text-foreground">Plans & featured</Link></li>
        </ul>
      </div>
      <div data-testid="footer-contact-section">
        <div className="eyebrow mb-3">Contact</div>
        <ul className="space-y-2.5 text-foreground/70 text-sm">
          <li>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              data-testid="footer-contact-email"
              className="inline-flex items-center gap-2 hover:text-foreground group"
            >
              <Mail
                className="w-3.5 h-3.5 text-accent shrink-0 group-hover:text-foreground"
                strokeWidth={1.6}
              />
              <span className="break-all">{CONTACT_EMAIL}</span>
            </a>
          </li>
          <li>
            <a
              href={`tel:+1${CONTACT_PHONE_RAW}`}
              data-testid="footer-contact-call"
              className="inline-flex items-center gap-2 hover:text-foreground group"
            >
              <Phone
                className="w-3.5 h-3.5 text-accent shrink-0 group-hover:text-foreground"
                strokeWidth={1.6}
              />
              <span>{CONTACT_PHONE_DISPLAY}</span>
            </a>
          </li>
          <li>
            <a
              href={`sms:+1${CONTACT_PHONE_RAW}`}
              data-testid="footer-contact-text"
              className="inline-flex items-center gap-2 hover:text-foreground group"
            >
              <MessageSquare
                className="w-3.5 h-3.5 text-accent shrink-0 group-hover:text-foreground"
                strokeWidth={1.6}
              />
              <span>Text us</span>
            </a>
          </li>
          <li className="text-[11px] text-muted-foreground pt-1">
            Call or text — fastest response, Mon–Sat
          </li>
        </ul>
      </div>
    </div>
    <div className="container-narrow text-[10px] uppercase tracking-[0.2em] text-muted-foreground pb-8 flex flex-wrap gap-5 items-center">
      <span>© Bud Link · v1 · Cannabis is for adults 21+</span>
      <Link to="/terms" className="hover:text-foreground" data-testid="footer-terms-link">Terms</Link>
      <Link to="/privacy" className="hover:text-foreground" data-testid="footer-privacy-link">Privacy</Link>
      <Link to="/21-plus" className="hover:text-foreground" data-testid="footer-age-link">21+ disclosure</Link>
    </div>
    <AskDispensaryDialog open={askOpen} onOpenChange={setAskOpen} />
  </footer>
  );
};
